import {
  type GridPaginationModel,
  type GridRowSelectionModel,
  type GridSortModel,
} from '@mui/x-data-grid-premium';
import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { defaultTaxCodesSearch } from '~/routes/(main)/settings/masterdata/tax-codes/-searchParams';

import { type TaxCodesQueryParams, useQueryTaxCodes } from '~/data/taxCode';

import {
  useUniversalFilterPayload,
  useUniversalFiltersSelector,
  useUniversalFiltersStore,
} from '~/modules/universalFilters';

import { DataTable } from '@vestigas/ui';

import { columns, SORT_FIELD_TO_ORDER_BY, type TaxCodeRow } from './columns';

const Route = getRouteApi('/(main)/settings/masterdata/tax-codes/');
const SORTING_ORDER: GridSortModel[number]['sort'][] = ['asc', 'desc'];

const EMPTY_ROWS: TaxCodeRow[] = [];
const getRowId = (row: TaxCodeRow) => row.id;

type P = {
  readonly onSelectionContextChange: () => void;
  readonly onRowSelectionModelChange: (
    selection: GridRowSelectionModel,
  ) => void;
  readonly rowSelectionModel: GridRowSelectionModel;
};

export function TaxCodesDataTable({
  onRowSelectionModelChange,
  onSelectionContextChange,
  rowSelectionModel,
}: P) {
  const navigate = Route.useNavigate();
  const query = Route.useSearch();
  const filterPayload = useUniversalFilterPayload<TaxCodesQueryParams>();
  const store = useUniversalFiltersStore();
  const selectedFilterId = useUniversalFiltersSelector(
    (context) => context.selectedFilterId,
  );
  const { limit, orderBy, page, sortOrder } = query;
  const offset = page * limit;

  const previousFilterPayloadRef = useRef<string | null>(null);
  const serializedFilterPayload = JSON.stringify(filterPayload);
  useEffect(() => {
    if (
      previousFilterPayloadRef.current !== null &&
      previousFilterPayloadRef.current !== serializedFilterPayload
    ) {
      void navigate({
        search: (prev) => ({
          ...prev,
          page: 0,
        }),
      });
    }

    previousFilterPayloadRef.current = serializedFilterPayload;
  }, [navigate, serializedFilterPayload]);

  const selectionContext = `${serializedFilterPayload}:${page}:${limit}:${orderBy}:${sortOrder}`;
  const previousSelectionContextRef = useRef(selectionContext);
  useEffect(() => {
    if (previousSelectionContextRef.current !== selectionContext) {
      onSelectionContextChange();
    }

    previousSelectionContextRef.current = selectionContext;
  }, [onSelectionContextChange, selectionContext]);

  const { data, isPending, isPlaceholderData } = useQueryTaxCodes({
    ...filterPayload,
    limit,
    offset,
    orderBy,
    sortOrder,
  });
  const totalCount = data?.totalCount ?? data?.paginatedCount ?? 0;

  useEffect(() => {
    if (isPlaceholderData) return;

    store.trigger.setFilterResultCount({
      count: totalCount,
      filterId: selectedFilterId,
    });
  }, [isPlaceholderData, selectedFilterId, store, totalCount]);

  const paginationModel = useMemo<GridPaginationModel>(
    () => ({
      page,
      pageSize: limit,
    }),
    [limit, page],
  );

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          limit: model.pageSize,
          page: model.page,
        }),
      });
    },
    [navigate],
  );

  const sortModel = useMemo<GridSortModel>(() => {
    const field = Object.entries(SORT_FIELD_TO_ORDER_BY).find(
      ([, value]) => value === orderBy,
    )?.[0];

    return field
      ? [{ field, sort: sortOrder === 'DESC' ? 'desc' : 'asc' }]
      : [];
  }, [orderBy, sortOrder]);

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      const [first] = model;
      const nextOrderBy =
        first &&
        SORT_FIELD_TO_ORDER_BY[
          first.field as keyof typeof SORT_FIELD_TO_ORDER_BY
        ];

      void navigate({
        search: (prev) => ({
          ...prev,
          orderBy: nextOrderBy ?? defaultTaxCodesSearch.orderBy,
          page: 0,
          sortOrder:
            first?.sort === 'desc' ? 'DESC' : defaultTaxCodesSearch.sortOrder,
        }),
      });
    },
    [navigate],
  );

  const onRowClick = useCallback(
    ({ row }: { row: TaxCodeRow }) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          taxCodeId: row.id,
        }),
      });
    },
    [navigate],
  );

  return (
    <div className="size-full overflow-clip rounded-lg border border-tiga-border-lighter bg-white">
      <DataTable<TaxCodeRow>
        checkboxSelection
        checkboxSelectionVisibleOnly
        columns={columns}
        disableRowSelectionExcludeModel
        disableRowSelectionOnClick
        getRowId={getRowId}
        height="100%"
        loading={isPending}
        onPaginationModelChange={onPaginationModelChange}
        onRowClick={onRowClick}
        onRowSelectionModelChange={onRowSelectionModelChange}
        onSortModelChange={onSortModelChange}
        paginationModel={paginationModel}
        rowCount={totalCount}
        rowSelectionModel={rowSelectionModel}
        rows={data?.data ?? EMPTY_ROWS}
        sortingOrder={SORTING_ORDER}
        sortModel={sortModel}
      />
    </div>
  );
}
